import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setSelectedRoom } from "redux/actions";
import DatePicker from "components/DatePicker";
import TimePicker from "components/TimePicker";
import Input from "components/Input";
import PhoneNumberInput from "components/PhoneNumberInput";
import Button from "components/Button";
import Dropdown from "components/Dropdown";
import StatusBar from "components/StatusBar";
import StatusTooltip from "components/StatusTooltip";
import ModalBookingCancel from "screens/ModalBookingCancel";
import ModalCancelConfirm from "screens/ModalCancelConfirm";
import ModalCreateKey from "screens/ModalCreateKey";
import ModalKeyPrinting from "screens/ModalKeycardPrinting";
import ModalResetWifi from "screens/ModalResetWifi";
import ModalInvalidateWifi from "screens/ModalInvalidateWifi";
import Card from "components/Card";
import CardHeader from "components/CardHeader";
import { Row, RowContainer, TravelInputRow } from "./styles";
import { useMutation } from "@apollo/client";
import {
  UPDATE_BOOKING_BY_BOOKING_ID,
  UPDATE_ROOM_STATUS,
  INVALIDATE_KEY,
  UNLOCK_DOOR,
} from "graphql/service";
import statusMap from "constants/statusMap";
import { OpenNewPageIcon } from "assets/icons";
import { 
  TIME_ZONE,
  DEFAULT_CHECK_IN_HOUR,
  DEFAULT_CHECK_IN_MINUTE,
  DEFAULT_CUT_OFF_HOUR,
  DEFAULT_CUT_OFF_MINUTE
} from "constants/constant";
import ModalCheckOutConfirm from "screens/ModalCheckOutConfirm";
import colors from "constants/colors";
import { debouncedErrorMessage } from "utils/messageUtil";
import { message } from "antd";
import moment from "moment-timezone";

const BookingInformation = ({ groupId, booking, refetch }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [checkOutOpen, setCheckOutOpen] = useState(false);
  const [confirmIsOpen, setConfirmIsOpen] = useState(false);
  const [cancelRoomParams, setCancelRoomParams] = useState({});

  const [bookingInfo, setBookingInfo] = useState({});
  const [bookingInfoError, setBookingInfoError] = useState({});

  const [phoneNumber, setPhoneNumber] = useState(booking.phoneNumber);
  const [phoneNumberValidity, setPhoneNumberValidity] = useState(false);
  
  useEffect(() => {
    setBookingInfo({
      bookingId: booking.bookingId,
      roomBookingId: booking.roomBookings[0]?.roomBookingId,
      roomType: booking.roomBookings[0]?.room.roomType,
      roomNumber: booking.roomBookings[0]?.room.roomNumber,
      roomStatus: booking.roomBookings[0]?.room?.roomStatusHistories?.[0]?.status,
      roomId: booking.roomBookings[0]?.room.roomId,
      checkInDate: booking.checkedInAt || booking.checkInDate,
      checkOutDate: booking.checkedOutAt || booking.checkOutDate,
      fullName: booking.fullName,
      phoneNumber: booking.phoneNumber,
      price: booking.roomBookings[0]?.room.pricePerNight,
      isWheelchairAccessible: booking.roomBookings[0]?.room.barrierFree,
      status: booking.status,
      cancelledAt: booking.cancelledAt,
      cancelledBy: booking.cancelledBy?.fullName,
      cancellationReason: booking.cancellationReason,
      cancellationFee: booking.cancellationFee,
    });
  }, [booking]);

  useEffect(() => {
    if (phoneNumberValidity) {
      if (phoneNumber !== bookingInfo.phoneNumber) {
        setBookingInfoError({ ...bookingInfoError, phoneNumberError: false });
        setBookingInfo({
          ...bookingInfo,
          phoneNumber: phoneNumber,
        })
      }
    }
  }, [phoneNumberValidity]);

  const [updateRoomStatus] = useMutation(UPDATE_ROOM_STATUS, {
    onCompleted: refetch,
    onError: (error) => {
      if (!error.networkError) {
        let content = error.message;
        if (error.message.includes("Validation")) {
          content = JSON.stringify(
            error.graphQLErrors[0]?.extensions?.exception
          );
        }
        debouncedErrorMessage(content);
      }
    },
  });

  const [unlockDoor, { loading: unlockDoorLoading }] = useMutation(
    UNLOCK_DOOR,
    {
      onCompleted: (data) => {
        if (data) {
          message.success("Door unlocked successfully!");
        }
      },
      onError: (error) => {
        if (!error.networkError) {
          let content = error.message;
          if (error.message.includes("Validation")) {
            content = JSON.stringify(
              error.graphQLErrors[0]?.extensions?.exception
            );
          }
          debouncedErrorMessage(content);
        }
      },
    }
  );

  const [invalidateKey, { loading: invalidateKeyLoading }] = useMutation(
    INVALIDATE_KEY,
    {
      onCompleted: (data) => {
        if (data) {
          message.success("Key invalidated successfully!");
        }
      },
      onError: (error) => {
        if (!error.networkError) {
          let content = error.message;
          if (error.message.includes("Validation")) {
            content = JSON.stringify(
              error.graphQLErrors[0]?.extensions?.exception
            );
          }
          debouncedErrorMessage(content);
        }
      },
    }
  );

  const [deviceId, setDeviceId] = useState("Kiosk 1");
  const [KeyPrintModalIsOpen, setKeyPrintModalIsOpen] = useState(false);

  const statusOptions = [
    "Room Inspected",
    "Room Clean",
    "Room Dirty",
    "Out of Service",
  ];

  const handleChangeRoomStatus = (e) => {
    const statusKey = Object.keys(statusMap).find(
      (key) => statusMap[key] === e
    );
    updateRoomStatus({
      variables: {
        input: {
          roomId: booking.roomBookings[0]?.room.roomId,
          status: statusKey,
        },
      },
    });
  };

  const handleUnlockDoor = () => {
    if (unlockDoorLoading) return;
    //FIXME: update this when the backend is ready
    // unlockDoor({
    //   variables: {
    //     input: {
    //       roomNumber: roomNumber
    //     }
    //   },
    // })
  };

  const handleInvalidateKey = () => {
    if (invalidateKeyLoading) return;
    //FIXME: update this when the backend is ready
    // invalidateKey({
    //   variables: {
    //     input: {
    //       roomNumber: roomNumber
    //     }
    //   },
    // })
  };

  const [updateBookingByBookingId, { loading }] = useMutation(
    UPDATE_BOOKING_BY_BOOKING_ID,
    {
      onCompleted: (updateBookingData) => {
        refetch();
        message.success("Booking updated successfully");
      },
      onError: (error) => {
        if (!error.networkError) {
          let content = error.message;
          if (error.message.includes("Validation")) {
            content = JSON.stringify(
              error.graphQLErrors[0]?.extensions?.exception
            );
          }
          debouncedErrorMessage(content);
        }
      },
    }
  );

  const handleSaveChanges = () => {
    if (loading) return;
    updateBookingByBookingId({
      variables: {
        bookingId: bookingInfo.bookingId,
        input: {
          fullName: bookingInfo.fullName,
          phoneNumber: bookingInfo.phoneNumber,
          checkInDate: bookingInfo.checkInDate,
          checkOutDate: bookingInfo.checkOutDate,
        },
      },
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="type">
          <div>
            <span>ROOM {booking.roomBookings[0]?.room?.roomNumber} </span>
            <span className="grey">
              {booking.roomBookings[0]?.room?.roomType?.name}
            </span>
          </div>
          <a
            className="icon"
            href={`https://pms.rookhotel.com/room-details/${bookingInfo.roomNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open room details"
          >
            <OpenNewPageIcon />
          </a>
          <StatusBar status={bookingInfo.status} />
          {bookingInfo.status === "CANCELLED" ? (
            <StatusTooltip
              createdAt={bookingInfo.cancelledAt}
              createdBy={bookingInfo.cancelledBy}
              type={"Cancellation"}
              reason={bookingInfo.cancellationReason}
              amount={bookingInfo.cancellationFee}
            />
          ) : null}
        </div>
      </CardHeader>
      <RowContainer>
        {(bookingInfo.status === "PENDING" || bookingInfo.status === "CHECKED_IN")? (
          <>
            <Row>
              <Button
                mainPage
                onClick={handleInvalidateKey}
                data-loading={invalidateKeyLoading}
                additionalstyle={`
                  width: 134px;

                  @media only screen and (min-width: 767px) and (max-width: 1200px) {
                    width: 163px;
                  }
                
                  @media only screen and (max-width: 767px) {
                    width: 100px;
                  }
                `}
              >
                {invalidateKeyLoading ? null : "Invalidate Key"}
              </Button>
              <ModalCreateKey
                mainPage
                buttonStyle={`
                  width: 133px;

                  @media only screen and (min-width: 767px) and (max-width: 1200px) {
                    width: 162px;
                  }
                
                  @media only screen and (max-width: 767px) {
                    width: 100px;
                  }
                `}
                roomNumber={bookingInfo.roomNumber}
                deviceId={deviceId}
                setDeviceId={setDeviceId}
                setKeyPrintModalIsOpen={setKeyPrintModalIsOpen}
              />
              <Button
                mainPage
                onClick={handleUnlockDoor}
                data-loading={unlockDoorLoading}
                additionalstyle={`
                  width: 123px;

                  @media only screen and (min-width: 767px) and (max-width: 1200px) {
                    width: 148px;
                  }
                
                  @media only screen and (max-width: 767px) {
                    width: 100px;
                  }
                `}
              >
                {unlockDoorLoading ? null : "Unlock Door"}
              </Button>
              {(bookingInfo.status === "CHECKED_IN") && <ModalResetWifi
                buttonStyle={`
                  width: 101px;
                `}
                roomBookingId={bookingInfo.roomBookingId}
              />}
              <ModalKeyPrinting
                isOpen={KeyPrintModalIsOpen}
                setIsOpen={setKeyPrintModalIsOpen}
                deviceId={deviceId}
              />
            </Row>
            <Row>
              <Dropdown
                mainPage
                title="ROOM STATUS"
                options={statusOptions}
                value={statusMap[bookingInfo.roomStatus]}
                disabled={statusMap[bookingInfo.roomStatus]?.includes('Occupied')}
                onChange={handleChangeRoomStatus}
                additionalstyle={`
                  width: 40%;
                `}
              />
              {bookingInfo.status==="PENDING" && <Button
                mainPage
                onClick={(event) => {
                  const earlyCheckIn = (moment().tz(TIME_ZONE) < moment(booking.checkInDate).tz(TIME_ZONE).set({ hour: DEFAULT_CHECK_IN_HOUR, minute: DEFAULT_CHECK_IN_MINUTE, second: 0 })) && (moment(booking.checkInDate).tz(TIME_ZONE).set({ hour: DEFAULT_CUT_OFF_HOUR, minute: DEFAULT_CUT_OFF_MINUTE, second: 0 }) <= moment().tz(TIME_ZONE))

                  if (earlyCheckIn) {
                    if (event.metaKey || event.ctrlKey) {
                      // Open new tab with absolute URL
                      window.open(`https://pms.rookhotel.com/group-check-in/early-check-in/${groupId}`, '_blank', 'noopener,noreferrer');
                    } else {
                      // Normal navigation
                      history.push(`/group-check-in/early-check-in/${groupId}`);
                    }
                  } else {
                    if (event.metaKey || event.ctrlKey) {
                      // Open new tab with absolute URL
                      window.open(`https://pms.rookhotel.com/group-check-in/confirm-identity/${groupId}`, '_blank', 'noopener,noreferrer');
                    } else {
                      history.push(`/group-check-in/confirm-identity/${groupId}`);
                    }
                  }
                }}
                additionalstyle={`
                  width: 82px;
                  margin-left: 8px;
                `}
              >
                Check In
              </Button>}
              {bookingInfo.status === "CHECKED_IN" ? (
                <ModalCheckOutConfirm
                  buttonLabel={"Check Out"}
                  buttonStyle={`
                      width: 86px;
                      margin-left: 8px;
                    `}
                  isOpen={checkOutOpen}
                  setIsOpen={setCheckOutOpen}
                  roomString={"room " + bookingInfo.roomNumber}
                  checkOutBookings={[booking]}
                  refetch={refetch}
                />
              ) : null}
              <ModalBookingCancel
                danger
                checkedIn={bookingInfo.status === "CHECKED_IN"}
                buttonLabel={"Cancel This Room"}
                buttonStyle={`
                    width: 130px;
                  `}
                setCancelRoomParams={setCancelRoomParams}
                setConfirmIsOpen={setConfirmIsOpen}
                roomNumber={bookingInfo.roomNumber}
              />
              <ModalCancelConfirm
                isOpen={confirmIsOpen}
                setIsOpen={setConfirmIsOpen}
                cancelRoomParams={cancelRoomParams}
                setCancelRoomParams={setCancelRoomParams}
                roomNumber={bookingInfo.roomNumber}
                bookingId={bookingInfo.bookingId}
                refetch={refetch}
              />
            </Row>
          </>
        ): null}
        {(bookingInfo.status === "CANCELLED" || bookingInfo.status === "CHECKED_OUT") ? (
          <Row>
            <ModalInvalidateWifi
              buttonStyle={`
                width: 126px;
              `}
              bookingId={bookingInfo.bookingId}
              checkedOut={bookingInfo.status === "CHECKED_OUT"}
            />
          </Row>
        ): null}
        <Row>
          <Input
            mainPage
            title="NAME"
            value={bookingInfo.fullName || ""}
            error={bookingInfoError.fullNameError}
            onChange={(e) => {
              dispatch(
                setBookingInfo({ ...bookingInfo, fullName: e.target.value })
              );
            }}
            additionalstyle={`
              width: 100%;

              @media only screen and (max-width: 767px) {
                width: 100%;
              }
            `}
          />
          <div className="phone">
            <PhoneNumberInput
              mainPage
              title="PHONE"
              error={bookingInfoError.phoneNumberError}
              initialValue={phoneNumber}
              onChangeNumber={setPhoneNumber}
              setPhoneNumberValidity={setPhoneNumberValidity}
              onBlur={() => {
                if (!phoneNumberValidity && phoneNumber.length > 0) {
                  setBookingInfoError({
                    ...bookingInfoError,
                    phoneNumberError: true,
                  });
                } else {
                  setBookingInfoError({
                    ...bookingInfoError,
                    phoneNumberError: false,
                  })
                }
              }}
            />
          </div>
        </Row>
        <TravelInputRow>
          <div className="infos">
            <div className="mobileRow">
              <Dropdown
                mainPage
                disabled
                title="ROOM #"
                value={bookingInfo.roomNumber}
                additionalstyle={`
                  width: 40%;
                  min-width: 40px;
                `}
              />
              <Dropdown
                mainPage
                disabled
                title="ROOM TYPE"
                value={bookingInfo.roomType?.name}
                additionalstyle={`
                  width: 60%;
                  min-width: 100px;
                `}
              />
            </div>
            <div className="mobileRow">
              <Dropdown
                mainPage
                options={[1, 2]}
                value={bookingInfo.roomType?.capacity}
                title="CAPACITY"
                disabled
                additionalstyle={`
                    width: 40%;
                    min-width: 30px;
                  `}
              />
              <Dropdown
                mainPage
                options={["true", "false"]}
                value={
                  bookingInfo.isWheelchairAccessible
                    ? bookingInfo.isWheelchairAccessible + ""
                    : "false"
                }
                title="BARRIER FREE"
                disabled
                additionalstyle={`
                    width: 60%;
                    min-width: 60px;
                  `}
              />
            </div>
          </div>
          {bookingInfo.status === "CHECKED_IN" ? (
            <Button
              mainPage
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey) {
                  // Open new tab with absolute URL
                  window.open(
                    `https://pms.rookhotel.com/room-details/${bookingInfo.roomId}`,
                    "_blank",
                    "noopener,noreferrer"
                  );
                } else {
                  dispatch(setSelectedRoom(null));
                  history.push(`/room-change/${bookingInfo.bookingId}`);
                }
              }}
              additionalstyle={`
                width: 112px;
              `}
            >
              Change Room
            </Button>
          ) : null}
        </TravelInputRow>
        <TravelInputRow>
          <div className="mobileRow">
            <div className="datePickerContainer">
              <span>ARRIVAL</span>
              <DatePicker
                error={
                  bookingInfo.checkInDate
                    ? bookingInfo.checkInDate >= bookingInfo.checkOutDate
                    : false
                }
                disabled={bookingInfo.status !== "PENDING"}
                minDate={moment(new Date()).tz(TIME_ZONE)}
                dateValue={moment(bookingInfo.checkInDate).tz(TIME_ZONE)}
                dateOnChange={(date) => {
                  setBookingInfo({ ...bookingInfo, checkInDate: date });
                }}
                additionalstyle={`
                  width: 100%;
                  min-width: 130px;
                  font-size: 18px;
                  font-weight: 600;
                  border: 1px solid ${colors.darkGrey};

                  input {
                    padding-right: 26px;
                  }
                `}
              />
              <span className="datePlaceHolder">MM/DD/YYYY</span>
            </div>
            <div className="timePickerContainer">
              <span>ARRIVAL TIME</span>
              <TimePicker
                disabled={bookingInfo.status !== "PENDING"}
                TimeValue={moment(bookingInfo.checkInDate).tz(TIME_ZONE)}
                setTime={(e) => {
                  setBookingInfo({ ...bookingInfo, checkInDate: e });
                }}
                additionalstyle={`
                  width: 100%;
                  min-width: 110px;
                  border: 1px solid ${colors.darkGrey};

                  input {
                    padding-right: 26px;
                  }
                `}
              />
              <span className="timePlaceHolder">HH:MM</span>
            </div>
          </div>
          <div className="mobileRow">
            <div className="datePickerContainer">
              <span>DEPARTURE</span>
              <DatePicker
                disabled={bookingInfo.status !== "PENDING" && bookingInfo.status !== "CHECKED_IN"}
                error={
                  bookingInfo.checkInDate
                    ? bookingInfo.checkInDate >= bookingInfo.checkOutDate
                    : false
                }
                minDate={moment(new Date()).tz(TIME_ZONE)}
                dateValue={moment(bookingInfo.checkOutDate).tz(TIME_ZONE)}
                dateOnChange={(date) => {
                  setBookingInfo({ ...bookingInfo, checkOutDate: date });
                }}
                additionalstyle={`
                  width: 100%;
                  min-width: 130px;
                  font-size: 18px;
                  font-weight: 600;
                  border: 1px solid ${colors.darkGrey};

                  input {
                    padding-right: 26px;
                  }
                `}
              />
              <span className="datePlaceHolder">MM/DD/YYYY</span>
            </div>
            <div className="timePickerContainer">
              <span>DEPARTURE TIME</span>
              <TimePicker
                disabled={bookingInfo.status !== "PENDING" && bookingInfo.status !== "CHECKED_IN"}
                TimeValue={moment(bookingInfo.checkOutDate).tz(TIME_ZONE)}
                setTime={(e) => {
                  setBookingInfo({ ...bookingInfo, checkOutDate: e });
                }}
                additionalstyle={`
                  width: 100%;
                  min-width: 110px;
                  border: 1px solid ${colors.darkGrey};

                  input {
                    padding-right: 26px;
                  }
                `}
              />
              <span className="timePlaceHolder">HH:MM</span>
            </div>
          </div>
        </TravelInputRow>
      </RowContainer>
      {bookingInfo.status === "CHECKED_IN" ||
        bookingInfo.status === "PENDING" ? (
        <div className="buttonContainer">
          <Button
            mainPage
            success
            onClick={handleSaveChanges}
            data-loading={loading}
            additionalstyle={`
                width: 108px;
                margin-top: 24px;
              `}
          >
            {loading ? null : "Save Changes"}
          </Button>
        </div>): null}
    </Card>
  );
};

export default BookingInformation;
